import { queryOptions, useQuery } from '@tanstack/react-query';

import { ContainerLiveMigrationSummaryStepEnum } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.costReport.getContainerLiveMigrationSummary;

const CONTAINER_LIVE_MIGRATION_SUMMARY_QUERY_KEY =
  'cluster-container-live-migration-summary';

const getContainerLiveMigrationSummaryQueryKey = ({
  organizationId,
  clusterId,
  startTime,
  endTime,
  timeZone,
  step,
}: {
  organizationId?: string;
  clusterId?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  step?: Parameters<typeof apiFn>[0]['step'];
}) =>
  [
    CONTAINER_LIVE_MIGRATION_SUMMARY_QUERY_KEY,
    organizationId,
    clusterId,
    startTime,
    endTime,
    timeZone,
    step,
  ] as const;

const getContainerLiveMigrationSummaryQueryOptions = ({
  organizationId,
  clusterId,
  startTime,
  endTime,
  timeZone,
  step,
  enabled = true,
}: {
  organizationId?: string;
  clusterId?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  step?: Parameters<typeof apiFn>[0]['step'];
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getContainerLiveMigrationSummaryQueryKey({
      organizationId,
      clusterId,
      startTime,
      endTime,
      timeZone,
      step,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId: organizationId!,
        clusterId: clusterId!,
        startTime,
        endTime,
        timeZone,
        step,
      });

      return data;
    },
    enabled: makeIsEnabled(
      !!organizationId && !!clusterId && !!startTime && !!endTime,
      enabled
    ),
  });

export const useContainerLiveMigrationSummaryQuery = ({
  organizationId,
  clusterId,
  startTime,
  endTime,
  timeZone,
  step = ContainerLiveMigrationSummaryStepEnum.ONE_DAY,
  enabled = true,
}: {
  organizationId?: string;
  clusterId?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  step?: Parameters<typeof apiFn>[0]['step'];
  enabled?: boolean;
}) => {
  return useQuery(
    getContainerLiveMigrationSummaryQueryOptions({
      organizationId,
      clusterId,
      startTime,
      endTime,
      timeZone,
      step,
      enabled,
    })
  );
};
